import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { FiUsers, FiVideo, FiBriefcase, FiAward, FiTarget, FiDollarSign } from 'react-icons/fi';
import './Features.css'; // Import the CSS file

const Features = () => {
  const featuresRef = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    featuresRef.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  const features = [
    {
      icon: <FiUsers />,
      title: 'No Interview Required',
      description: 'Direct enrollment after training. No stressful interview process.',
      color: '#000000'
    },
    {
      icon: <FiVideo />,
      title: 'Live Training Sessions',
      description: 'Interactive live sessions with industry experts for 1 month.',
      color: '#1a1a1a'
    },
    {
      icon: <FiBriefcase />,
      title: 'Hands-on Internship',
      description: '1 month internship with real industry projects and guidance.',
      color: '#2d2d2d'
    },
    {
      icon: <FiAward />,
      title: 'Dual Certificates',
      description: 'Separate verified certificates for training and internship.',
      color: '#000000'
    },
    {
      icon: <FiTarget />,
      title: 'Career Bonuses',
      description: 'Resume building, LinkedIn optimization, interview preparation.',
      color: '#1a1a1a'
    },
    {
      icon: <FiDollarSign />,
      title: 'Affordable Fee',
      description: 'Complete program at just ₹599 with maximum value.',
      color: '#2d2d2d'
    }
  ];

  return (
    <section id="features" className="features-section section-padding gradient-bg">
      <div className="features-container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="features-header"
        >
          <h2 className="section-title">Program Features</h2>
          <p className="section-subtitle">
            Everything you need to kickstart your career in one comprehensive program
          </p>
        </motion.div>

        <div className="features-grid">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              ref={(el) => (featuresRef.current[index] = el)}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="feature-card fade-in"
              style={{ '--feature-color': feature.color }} // Pass color via CSS variable
            >
              <div className="icon-container">
                <div className="icon">
                  {React.cloneElement(feature.icon, {
                    size: 32,
                    color: '#ffffff'
                  })}
                </div>
              </div>
              <h3 className="feature-title">{feature.title}</h3>
              <p className="feature-description">{feature.description}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          viewport={{ once: true }}
          className="cta-section"
        >
          <div className="cta-card">
            <h3 className="cta-title">Ready to Transform Your Career?</h3>
            <p className="cta-text">
              Join 1000+ students who have accelerated their careers with our program
            </p>
            <div className="cta-stats">
              <div className="stat">
                <div className="stat-number">₹599</div>
                <div className="stat-label">Only</div>
              </div>
              <div className="stat-divider"></div>
              <div className="stat">
                <div className="stat-number">2</div>
                <div className="stat-label">Months</div>
              </div>
              <div className="stat-divider"></div>
              <div className="stat">
                <div className="stat-number">2</div>
                <div className="stat-label">Certificates</div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Features;